import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { AuthenticatedRoute } from '@/app/constant';
import { AppRouteBody } from '@/components/AppRouteBody';
import { technologyExportConfig } from '@/components/export/configs/technology.export.config';
import {
  buildActiveFilters,
  UniversalExportModal,
} from '@/components/export/universal';
import { FilterDropdown } from '@/components/filters/FilterDropdown';
import type { FilterOption, GroupOption } from '@/components/filters/types';
import { ClearFiltersButton } from '@/components/legacy-ui/ClearFiltersButton';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { GetStartedModal } from '@/components/modals/GetStartedModal';
import { NoteModal } from '@/components/modals/NoteModal';
import { BulkConfirmationDialog } from '@/components/table/BulkConfirmationDialog';
import { CanonicalTable } from '@/components/table/CanonicalTable';
import { SelectionBanner } from '@/components/table/SelectionBanner';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGetTechnologyFilters } from '@/hooks/api/useGetTechnologyFilters';
import { useActionHotkey } from '@/hooks/hotkeys';
import { useBulkConfirmation } from '@/hooks/useBulkConfirmation/useBulkConfirmation';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useHasActiveFilters } from '@/hooks/useHasActiveFilters';
import { useMy } from '@/hooks/useMy';
import { useTableRemountOnTransition } from '@/hooks/useTableRemountOnTransition';
import { useTabParam } from '@/hooks/useTabParam';
import { Filter, useGetTechnologies } from '@/hooks/useTechnology';
import { ImportAssetsModal } from '@/sections/asset/components/modals/ImportAssetsModal';
import {
  ASSET_GET_STARTED_METHODS,
  type AssetGetStartedMethodId,
} from '@/sections/asset/config/getStartedMethods';
import { AddSeedByTypeModal } from '@/sections/seeds/components/modals/AddSeedByTypeModal';
import { TechnologyActions } from '@/sections/technology/components/actions';
import {
  TechnologyRemoveTag,
  TechnologyTag,
} from '@/sections/technology/components/modals';
import { getFilterDropdownWidth } from '@/sections/technology/config';
import {
  useEffectiveTechnologyFilter,
  useTechnologyActions,
  useTechnologyColumns,
} from '@/sections/technology/hooks';
import { BulkActivityAnnouncer } from '@/sections/topNavBar/BulkJobsDrawer/BulkActivityAnnouncer';
import { WebpagesContent } from '@/sections/webpages';
import { useBulkJobsStore } from '@/state/bulkJobs.store';
import { useGlobalState } from '@/state/global.state';
import { GraphQuery, Technology } from '@/types';
import { useEntityFilters } from '@/utils/entityFilters.util';
import { capitalize, omit } from '@/utils/lodash.util';
import {
  collapseMergedFilterValues,
  expandMergedFilterValues,
} from '@/utils/mergeFilterOptions.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

import { getTechnologyNoDataConfig } from './utils/getTechnologyNoDataConfig';

const technologyTabs = [
  { value: 'services', label: 'Services' },
  { value: 'webapplications', label: 'Screenshots' },
];

export function Techonologies() {
  const navigate = useNavigate();
  const { drawerState } = useGlobalState();
  const queryClient = useQueryClient();
  const { canWriteAssets } = useEntitlements();
  const isKeyPending = useBulkJobsStore(state => state.isKeyPending);

  const [typeParam, setTypeParam] = useTabParam({
    validValues: ['services', 'grid', 'table'],
    defaultValue: 'services',
    param: 'type',
  });
  const activeTab = typeParam === 'services' ? 'services' : 'webapplications';

  // FIX #5 tab-view-reset: remember the last Screenshots sub-view (grid vs
  // table) so a Services round-trip restores it instead of hardcoding 'table'.
  // Latest-ref updated during render (house pattern, see useActionHotkey.ts):
  // it tracks the current grid/table choice and, seeded from typeParam, defaults
  // to 'grid' (Contact Sheet) on first entry from Services.
  const lastWebView = useRef<'grid' | 'table'>(
    typeParam === 'table' ? 'table' : 'grid'
  );
  if (typeParam === 'grid' || typeParam === 'table') {
    // Intentional render-phase latest-ref write mirroring the URL sub-view so
    // onValueChange can restore it without an extra render/effect.
    // eslint-disable-next-line react-hooks/refs -- house pattern, see useActionHotkey.ts
    lastWebView.current = typeParam;
  }

  // GetStartedModal workflow state
  const [isGetStartedModalOpen, setIsGetStartedModalOpen] = useState(false);
  const [isAddSeedModalOpen, setIsAddSeedModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const [filter, setFilter] = useStorage<Filter>(
    {
      queryKey: 'technologyFilters',
      key: StorageKey.FILTERS_TECHNOLOGIES,
      ephemeralKeys: ['search'],
    },
    {
      sort: {
        orderBy: 'name',
        descending: true,
      },
    }
  );

  const { data: propertyFilters, status: propertyFiltersStatus } =
    useGetTechnologyFilters();

  // Reconcile persisted merged-key filters (vendor/product/version) against
  // the current merged options so the query matches every raw variant
  // (ENG-4329). DERIVE-ONLY: the stored `filter` is never written back and
  // keeps feeding useStorage, the filterKey remount memo, export pills, and
  // dropdown selectedValues below.
  const effectiveFilter = useEffectiveTechnologyFilter(
    filter,
    propertyFilters,
    propertyFiltersStatus
  );

  const {
    data: technologies,
    status: technologiesStatus,
    error: technologiesError,
    isAllEmpty,
    isFilteredEmpty,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    hasNextPage,
    activeQuery,
  } = useGetTechnologies({ filter: effectiveFilter });

  // Detect initial loading state to prevent table UI flicker
  const isInitialLoading = technologiesStatus === 'pending';

  // Determine if any filters are currently active
  const hasActiveFilters = useHasActiveFilters({
    search: filter.search,
    advancedFilters: filter.properties,
  });

  // Filter options for export modal pill labels (cached — no extra API call)
  const { filterOptions: techFilterOptions, sortedOptions: techSortedOptions } =
    useEntityFilters('Technology');
  const techFieldLabels = useMemo(
    () => Object.fromEntries(techSortedOptions.map(o => [o.value, o.label])),
    [techSortedOptions]
  );

  // Use custom hook with smart column positioning
  const {
    columns,
    activeColumns,
    setActiveColumns,
    placeholder,
    sortableColumnsConfig,
  } = useTechnologyColumns();

  // Row selection management — uses useBulkSelection for Gmail-style two-step selection
  const bulkSelection = useBulkSelection({
    data: technologies,
    activeFilters: (activeQuery ?? {}) as GraphQuery,
    filterVersion: filter,
  });

  const {
    selectedRows: selectedKeys,
    setSelectedRows: setSelectedKeys,
    hasSelection: isRowsSelected,
    clearSelection,
    isSelectAll,
    isAllLoadedSelected,
    selectAllMatching,
    selectedCount,
    toggleKey,
    state: selectionState,
  } = bulkSelection;

  // Derive selectedTechnologies from selectedRows for action handlers
  const selectedTechnologies = technologies.filter(t =>
    selectedKeys.includes(t.key)
  );

  // Fetch total technology count from database via statistics
  // Backend creates: type="technology", name="vendor" with counts per vendor
  // Sum all vendor counts to get total technology count
  const { data: technologyStatistics } = useMy(
    {
      resource: 'statistic',
      query: '#technology#vendor#now#',
    },
    {
      staleTime: 60000, // Cache for 60 seconds
      refetchInterval: false, // Override default 30s polling
    }
  );

  // Compute total count from statistics response
  // Each vendor has a count - sum them for total technologies
  const totalDatabaseCount = useMemo(() => {
    if (!technologyStatistics) return 0;
    return technologyStatistics.reduce(
      (sum, item) => sum + (item.data?.count || 0),
      0
    );
  }, [technologyStatistics]);

  // Compute export active filters for UniversalExportModal
  const exportActiveFilters = useMemo(
    () =>
      buildActiveFilters(
        filter.properties as Record<string, unknown> | undefined,
        (techFilterOptions as Record<string, (FilterOption | GroupOption)[]>) ??
          {},
        filter.search,
        techFieldLabels
      ),
    [filter.search, filter.properties, techFilterOptions, techFieldLabels]
  );

  // Bulk confirmation for the tag bulk endpoint.
  // NOTE: does NOT call queryClient.invalidateQueries — that is handled by
  // useActiveBulkJobs (polls job status and invalidates on terminal state).
  const tagConfirm = useBulkConfirmation({
    resource: 'technology',
    action: 'tag',
    bulkSelection,
    clearSelection,
  });

  // Bulk confirmation for the untag bulk endpoint.
  // Same shape as tagConfirm above — uses resource:'technology', action:'untag'.
  // Wire format locked: action='untag', params:{tag}.
  const untagConfirm = useBulkConfirmation({
    resource: 'technology',
    action: 'untag',
    bulkSelection,
    clearSelection,
  });

  // Actions hook with modal state and handlers
  const { modalState, modalHandlers, handlers, selectedRowsCount } =
    useTechnologyActions({
      selectedTechnologies,
      selectedRowsCount: selectedCount,
      invalidateTechnologies: () => {
        queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'technology';
          },
          refetchType: 'active',
        });
      },
      setConfirmStatus: () => {
        // Placeholder for confirm status handling
      },
      selectionState,
      onBulkRequested: (tag: string) => {
        tagConfirm.openConfirmation({
          action: 'tag',
          actionName: `tag "${tag}"`,
          params: { tag },
        });
      },
      onBulkUntagRequested: (tag: string) => {
        untagConfirm.openConfirmation({
          action: 'untag',
          actionName: 'untag',
          params: { tag },
        });
      },
    });

  // Handle Get Started method selection
  const handleGetStartedMethodSelect = useCallback(
    (methodId: AssetGetStartedMethodId) => {
      setIsGetStartedModalOpen(false);

      switch (methodId) {
        case 'integrations': {
          // Build URL with drawer state params for atomic navigation + drawer open
          const drawerParams = new URLSearchParams();
          drawerParams.set('drawerCompositeKey', 'integration');
          drawerParams.set('drawerOrder', JSON.stringify(['integration']));

          navigate(
            `../${AuthenticatedRoute.INTEGRATIONS}?${drawerParams.toString()}`,
            { relative: 'path' }
          );
          break;
        }
        case 'seeds':
          setIsAddSeedModalOpen(true);
          break;
        case 'import':
          setIsImportModalOpen(true);
          break;
      }
    },
    [navigate]
  );

  // Force table remount on empty → data transition (fixes rendering bug)
  const tableKey = useTableRemountOnTransition(
    isFilteredEmpty,
    technologies.length
  );

  // Generate stable key based on filter/sort state to force remount on changes
  // This resets scroll position and clears stale virtualizer state
  // Note: search is excluded - debounced at API level, shouldn't trigger remounts
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        properties: filter.properties,
        sort: filter.sort,
      }),
    [filter.properties, filter.sort]
  );

  // Combine with existing tableKey for empty→data transitions
  const combinedTableKey = `${tableKey}-${filterKey}`;

  // Keyboard shortcuts for bulk actions — only active when rows are selected.
  // Migrated from a hand-rolled window keydown listener (ENG-43). The
  // input/textarea/contentEditable guard is now free via enableOnFormTags:false
  // (the useActionHotkey default), and the `global` scope auto-suppresses these
  // while a modal is open.
  //
  // c — open note modal (requires write_assets)
  useActionHotkey('c', () => modalHandlers.openNoteModal(), {
    enabled: isRowsSelected && canWriteAssets,
    deps: [modalHandlers],
  });

  // t — open tag modal (requires write_assets)
  useActionHotkey('t', () => modalHandlers.openTagModal(), {
    enabled: isRowsSelected && canWriteAssets,
    deps: [modalHandlers],
  });

  // e — export selected (no write gate)
  useActionHotkey('e', () => modalHandlers.openExportModal(), {
    enabled: isRowsSelected,
    deps: [modalHandlers],
  });

  return (
    <AppRouteBody title="Technologies" data-tutorial="technologies-section">
      <Tabs
        value={activeTab}
        onValueChange={tab =>
          setTypeParam(tab === 'services' ? 'services' : lastWebView.current)
        }
        className="flex h-full min-h-0 flex-col"
      >
        <div className="mb-4">
          <TabsList variant="boxed">
            {technologyTabs.map(tab => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                variant="boxed"
                data-tutorial={`tab-${tab.value}`}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <TabsContent
          value="webapplications"
          className="flex min-h-0 flex-1 flex-col"
        >
          <WebpagesContent
            viewMode={typeParam === 'table' ? 'table' : 'grid'}
            setViewMode={setTypeParam}
          />
        </TabsContent>
        <TabsContent value="services" className="flex min-h-0 flex-1 flex-col">
          {isInitialLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner size="xl" />
            </div>
          ) : (
            <>
              {!isAllEmpty && (
                <TableControlsContainer
                  data-testid="table-controls"
                  filterLabel={true}
                  filters={
                    <>
                      {Object.entries(propertyFilters).map(
                        ([key, propertyFilterOptions], index) => (
                          <div
                            key={index}
                            data-tutorial={`technologies-filter-${key}`}
                          >
                            <FilterDropdown
                              id={key}
                              isLoading={propertyFiltersStatus === 'pending'}
                              label={capitalize(key)}
                              options={propertyFilterOptions}
                              // Stored filter state is RAW backend values;
                              // collapse to canonical option values for display
                              // and expand selections back to every raw variant
                              // at apply time (mergeFilterOptions.util.ts).
                              // Options without rawValues (tags) make both
                              // calls a pass-through no-op.
                              selectedValues={collapseMergedFilterValues(
                                filter?.properties?.[key] || [],
                                propertyFilterOptions
                              )}
                              onApply={selected => {
                                setFilter(prev => {
                                  return {
                                    ...prev,
                                    properties:
                                      selected.length > 0
                                        ? {
                                            ...prev?.properties,
                                            [key]: expandMergedFilterValues(
                                              selected.map(option =>
                                                String(option.value ?? '')
                                              ),
                                              propertyFilterOptions
                                            ),
                                          }
                                        : omit(prev?.properties, key),
                                  };
                                });
                              }}
                              onClear={() => {
                                setFilter(prev => ({
                                  ...prev,
                                  properties: omit(prev?.properties, key),
                                  negatedFilterKeys:
                                    prev?.negatedFilterKeys?.filter(
                                      k => k !== key
                                    ),
                                }));
                              }}
                              width={getFilterDropdownWidth(key)}
                              isNegated={
                                filter?.negatedFilterKeys?.includes(key) ??
                                false
                              }
                              onToggleNegation={() => {
                                setFilter(prev => ({
                                  ...prev,
                                  negatedFilterKeys:
                                    prev?.negatedFilterKeys?.includes(key)
                                      ? prev.negatedFilterKeys.filter(
                                          k => k !== key
                                        )
                                      : [
                                          ...(prev?.negatedFilterKeys ?? []),
                                          key,
                                        ],
                                }));
                              }}
                            />
                          </div>
                        )
                      )}
                      {/* ENG-5107: reuse the already-computed hasActiveFilters
                          (same search + properties inputs) rather than
                          re-deriving the gate inline. */}
                      {hasActiveFilters && (
                        <ClearFiltersButton
                          onClick={() =>
                            setFilter(filters => {
                              return {
                                ...filters,
                                search: undefined,
                                properties: {},
                                negatedFilterKeys: [],
                              };
                            })
                          }
                        />
                      )}
                    </>
                  }
                  filtersRight={
                    <div data-tutorial="technologies-search">
                      <SearchBar
                        size="sm"
                        className="h-fit w-64"
                        placeholder="Search technologies"
                        value={filter?.search}
                        onChange={value => {
                          setFilter(filter => ({
                            ...filter,
                            search: value,
                          }));
                        }}
                      />
                    </div>
                  }
                  selectionBar={
                    <>
                      <TableSelectionBar
                        embedded={true}
                        alwaysVisible={true}
                        isVisible={isRowsSelected}
                        selectedCount={selectedCount}
                        totalCount={technologies.length}
                        hasNextPage={hasNextPage}
                        isSelectAll={isSelectAll}
                        totalDatabaseLabel={`${totalDatabaseCount.toLocaleString()} total technologies`}
                        resourceLabel="technologies"
                        emptySelectionMessage="Select technologies to perform bulk actions"
                        actions={
                          <TechnologyActions
                            selectedTechnologies={selectedTechnologies}
                            selectedRowsCount={selectedRowsCount}
                            modalHandlers={modalHandlers}
                            actionHandlers={handlers}
                            onClearSelection={clearSelection}
                            hideActions={false}
                          />
                        }
                      />
                      <SelectionBanner
                        selectedCount={selectedCount}
                        totalLoadedCount={technologies.length}
                        hasNextPage={hasNextPage}
                        isSelectAll={isSelectAll}
                        isAllLoadedSelected={isAllLoadedSelected}
                        resourceLabel="technologies"
                        onSelectAllMatching={selectAllMatching}
                        onClearSelection={clearSelection}
                        enableSelectAllMatching={activeQuery != null}
                      />
                    </>
                  }
                />
              )}
              {/* Single table-scoped aria-live region for bulk action status.
              UX divergence #6: one region per table, NOT one per row. */}
              <BulkActivityAnnouncer />
              <CanonicalTable<Technology>
                key={combinedTableKey}
                keyboardNavigation={true}
                responsive={false}
                onRowClick={technology => {
                  drawerState.handleOpenTechnologyDrawer(technology);
                }}
                rowActions={technology => {
                  return {
                    menu: {
                      items: [
                        ...(canWriteAssets
                          ? [
                              {
                                label: 'Add Note',
                                onClick: () => {
                                  // Select this technology if not already selected
                                  if (!selectedKeys.includes(technology.key)) {
                                    toggleKey(technology.key);
                                  }
                                  modalHandlers.openNoteModal();
                                },
                              },
                              {
                                label: 'Add Tag',
                                onClick: () => {
                                  if (!selectedKeys.includes(technology.key)) {
                                    toggleKey(technology.key);
                                  }
                                  modalHandlers.openTagModal();
                                },
                              },
                              {
                                label: 'Remove Tag',
                                onClick: () => {
                                  if (!selectedKeys.includes(technology.key)) {
                                    toggleKey(technology.key);
                                  }
                                  modalHandlers.openRemoveTagModal();
                                },
                              },
                              {
                                label: '' as const,
                                isDivider: true,
                              },
                            ]
                          : []),
                        {
                          label: 'Export',
                          onClick: () => {
                            if (!selectedKeys.includes(technology.key)) {
                              toggleKey(technology.key);
                            }
                            // Export will happen with next render when selection is updated
                            setTimeout(
                              () => modalHandlers.openExportModal(),
                              100
                            );
                          },
                        },
                      ],
                    },
                  };
                }}
                sortableColumns={{
                  activeColumns,
                  setActiveColumns,
                  defaultConfig: sortableColumnsConfig,
                  placeholder,
                }}
                sort={
                  filter?.sort && {
                    ...filter.sort,
                    onSort: sort => {
                      setFilter(prev => ({ ...prev, sort }));
                    },
                  }
                }
                selection={{
                  value: selectedKeys,
                  onChange: setSelectedKeys,
                }}
                rowHeight={64}
                name="technologies"
                columns={columns}
                data={technologies}
                rowClassName={(technology: Technology) =>
                  isKeyPending(technology.key) ? 'opacity-70 cursor-wait' : ''
                }
                getRowProps={(technology: Technology) =>
                  isKeyPending(technology.key) ? { 'aria-busy': true } : {}
                }
                error={technologiesError}
                status={technologiesStatus}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
                isFetching={isFetching}
                hasNextPage={hasNextPage}
                noData={getTechnologyNoDataConfig({
                  isAllEmpty,
                  isFilteredEmpty,
                  hasActiveFilters,
                  onClearFilters: () =>
                    setFilter(prev => ({
                      ...prev,
                      search: undefined,
                      properties: undefined,
                    })),
                  onGetStarted: canWriteAssets
                    ? () => setIsGetStartedModalOpen(true)
                    : undefined,
                })}
              />

              {/* Modals - Migrated to shared NoteModal (2025-10-23) */}
              <NoteModal
                open={modalState.noteModal}
                onClose={modalHandlers.closeNoteModal}
                onSubmit={handlers.handleNote}
                resourceType="Technology"
                mode="markdown"
                presentation="modal"
                selectedCount={selectedRowsCount}
              />

              <TechnologyTag
                open={modalState.tagModal}
                onClose={modalHandlers.closeTagModal}
                technologyKeys={selectedKeys}
                currentTags={[]}
                onSuccess={clearSelection}
              />

              <TechnologyRemoveTag
                open={modalState.removeTagModal}
                onClose={modalHandlers.closeRemoveTagModal}
                technologyKeys={selectedKeys}
                onSuccess={clearSelection}
              />

              <UniversalExportModal
                config={technologyExportConfig}
                open={modalState.exportModal}
                onClose={modalHandlers.closeExportModal}
                selectedRows={
                  (isSelectAll
                    ? []
                    : selectedKeys) as unknown as readonly import('@/components/export/universal/types').TechnologyKey[]
                }
                onClearSelection={clearSelection}
                activeFilters={exportActiveFilters}
                activeQuery={activeQuery as GraphQuery | undefined}
              />

              {/* GetStartedModal workflow - onboarding modals for first-time users */}
              <GetStartedModal
                open={isGetStartedModalOpen}
                onClose={() => setIsGetStartedModalOpen(false)}
                onSelectMethod={handleGetStartedMethodSelect}
                title="Get Started with Technologies"
                subtitle="Technologies are discovered when you populate your attack surface:"
                methods={ASSET_GET_STARTED_METHODS}
              />

              <AddSeedByTypeModal
                open={isAddSeedModalOpen}
                onClose={() => setIsAddSeedModalOpen(false)}
                onSuccess={() => {
                  queryClient.invalidateQueries({
                    predicate: query => {
                      const queryKey = query.queryKey as string[];
                      return (
                        queryKey[0] === 'MY' && queryKey[1] === 'technology'
                      );
                    },
                    refetchType: 'active',
                  });
                  setIsAddSeedModalOpen(false);
                }}
              />

              <ImportAssetsModal
                open={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onImport={() => {
                  queryClient.invalidateQueries({
                    predicate: query => {
                      const queryKey = query.queryKey as string[];
                      return (
                        queryKey[0] === 'MY' && queryKey[1] === 'technology'
                      );
                    },
                    refetchType: 'active',
                  });
                  setIsImportModalOpen(false);
                }}
              />
              <BulkConfirmationDialog
                isOpen={tagConfirm.confirmPayload !== null}
                actionName={tagConfirm.confirmPayload?.actionName ?? ''}
                resourceLabel="technologies"
                isSelectAll={selectionState.mode === 'all'}
                itemCount={selectedCount}
                isPending={tagConfirm.isPending}
                onCancel={tagConfirm.closeConfirmation}
                onConfirm={tagConfirm.submitConfirmed}
              />
              <BulkConfirmationDialog
                isOpen={untagConfirm.confirmPayload !== null}
                actionName={untagConfirm.confirmPayload?.actionName ?? ''}
                resourceLabel="technologies"
                isSelectAll={selectionState.mode === 'all'}
                itemCount={selectedCount}
                isPending={untagConfirm.isPending}
                onCancel={untagConfirm.closeConfirmation}
                onConfirm={untagConfirm.submitConfirmed}
              />
            </>
          )}
        </TabsContent>
      </Tabs>
    </AppRouteBody>
  );
}
